'use client';

import React, { useEffect, useState, useRef, Suspense } from 'react';
import { set } from 'react-hook-form';
import Magnifier from 'react-magnifier';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';

interface ProductDetailImageCarouselProps {
  imageList: string[] | null;
  currentIndex: number;
  onClose: () => void;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
}

const ProductDetailImageCarousel = ({ imageList, currentIndex, onClose, setCurrentIndex }: ProductDetailImageCarouselProps) => {
  if (!imageList || imageList.length === 0) return null;

  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Pinch zoom states
  const containerRef = useRef<HTMLDivElement>(null);

  // Indicator animation states
  const [showZoomIndicator, setShowZoomIndicator] = useState(true);
  const [zoomIndicatorScale, setZoomIndicatorScale] = useState(1);
  const [indicatorScale, setIndicatorScale] = useState(1);
  const [allowSwapNavigation, setAllowSwapNavigation] = useState(true);

  // Add these states for swipe detection
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1280);
    };

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    checkMobile();
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    window.addEventListener('resize', checkMobile);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      handleClose();
    };

    if (isVisible) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isVisible]);

  useEffect(() => {
    if (isMobile) {
      let animationInterval: ReturnType<typeof setInterval>;

      const hideTimeout = setTimeout(() => {
        setShowZoomIndicator(false);
      }, 2000);

      animationInterval = setInterval(() => {
        setIndicatorScale((prev) => (prev === 1 ? 1.2 : 1));
      }, 500);

      return () => {
        clearTimeout(hideTimeout);
        clearInterval(animationInterval);
      };
    }
  }, [isMobile, currentIndex]);

  const totalImages = imageList.length;

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for transition to complete before actually closing
  };

  const prevImage = () => {
    setCurrentIndex((prev: number) => (prev === 0 ? totalImages - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentIndex((prev: number) => (prev === totalImages - 1 ? 0 : prev + 1));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX !== null && e.changedTouches?.length > 0 && allowSwapNavigation) {
      const distance = touchStartX - e.changedTouches[0].clientX;
      if (distance > 10) {
        nextImage();
      } else if (distance < -10) {
        prevImage();
      }
    }
    setTouchStartX(null);
  };

  return (
    <div
      className={`fixed inset-0 z-[1500] flex items-center justify-center overflow-hidden bg-[rgba(0,0,0,0.85)] transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleClose}
    >
      <button
        data-testid="carousel-close-button"
        className="focus:outline-hidden absolute right-4 top-4 z-[20] bg-transparent text-3xl font-bold text-white transition-transform hover:scale-110"
        onClick={handleClose}
      >
        ✕
      </button>

      <div
        className="absolute left-4 top-4 z-[20] text-lg text-white"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {currentIndex + 1} / {totalImages}
      </div>

      {totalImages > 1 && (
        <>
          <button
            data-testid="carousel-prev-button"
            className="prev-btn absolute left-4 z-[20] flex h-[44px] w-[44px] items-center justify-center bg-[rgba(30,30,30,0.8)] bg-[length:24px_24px] bg-center bg-no-repeat text-3xl font-bold text-gray-300 transition-transform hover:scale-110"
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
          >
            <span className="material-icons">arrow_back</span>
          </button>
          <button
            data-testid="carousel-next-button"
            className="next-btn absolute right-4 !z-[99999] flex h-[44px] w-[44px] items-center justify-center bg-[rgba(30,30,30,0.8)] bg-[length:24px_24px] bg-center bg-no-repeat text-3xl font-bold text-gray-300"
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
          >
            <span className="material-icons">arrow_forward</span>
          </button>
        </>
      )}

      <div
        className={`absolute transform transition-transform duration-300 ${isVisible ? 'scale-100' : 'scale-95'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {isMobile ? (
          <div
            ref={containerRef}
            className="relative z-10 flex h-full w-full touch-none items-center justify-center overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{ touchAction: 'none' }} // important
          >
            <div className="flex transition-transform duration-500 ease-in-out">
              <TransformWrapper
                minScale={1}
                maxScale={3}
                limitToBounds={true}
                centerZoomedOut={true}
                disablePadding={true}
                onZoomStop={({ state }) => {
                  // Zoom gesture ended, check scale now
                  console.log(state.scale, 'scale');
                  setZoomIndicatorScale(state.scale);
                  if (state.scale > 1.05) {
                    setShowZoomIndicator(false);
                    setAllowSwapNavigation(false);
                  } else {
                    setAllowSwapNavigation(true);
                  }
                }}
                initialScale={1}
                initialPositionX={0}
                initialPositionY={0}
                key={currentIndex}
              >
                {() => (
                  <TransformComponent>
                    <img
                      src={imageList[currentIndex]}
                      alt={`Product ${currentIndex + 1}`}
                      className="max-h-[90vh] w-full max-w-[100vw] flex-shrink-0 object-contain"
                      style={{
                        touchAction: 'none',
                      }}
                    />
                  </TransformComponent>
                )}
              </TransformWrapper>
            </div>

            {/* Zoom indicator overlay (only shows for 2 seconds) */}
            {showZoomIndicator && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div
                  className="flex flex-col items-center justify-center rounded-full bg-opacity-60 p-4 transition-transform duration-500"
                  style={{ transform: `scale(${indicatorScale})` }}
                >
                  <svg fill="none" height="60" viewBox="0 0 24 25" width="60" xmlns="http://www.w3.org/2000/svg">
                    <g clipRule="evenodd" fill="white" fillRule="evenodd">
                      <path d="m14.3333 4.0271c-.4411 0-.8265.29781-.9379.7246l-2.6647 10.2148c-.0652.2502-.2548.4492-.5015.5264-.2468.0773-.51595.022-.71228-.1462l-2.13389-1.8291c-.42482-.3641-1.06683-.3038-1.41636.1331-.2908.3635-.28885.8806.00471 1.2419l3.3585 4.1335c1.07652 1.325 1.49302 1.8166 2.02802 2.1072.1014.055.2057.1047.3124.1487.5629.232 1.2071.2452 2.9143.2452h1.0489c1.2845 0 1.7751-.0071 2.1906-.1265.7247-.2084 1.3543-.6622 1.7811-1.2837.2447-.3565.4066-.8197.8128-2.0382.0301-.0904.0592-.1777.0874-.2621.563-1.6869.7445-2.2309.7445-3.0659 0-1.1369-.6038-1.9328-1.5702-2.5118-.2381-.1426-.5579-.2591-1.0914-.375-.5376-.1168-1.2325-.2214-2.2067-.3675l-.0071-.0011-1.6872-.2697c-.2058-.0329-.3886-.1499-.5046-.3231-.1161-.1731-.1548-.3867-.107-.5896l1.201-5.09423c.1435-.60852-.3182-1.19167-.9434-1.19167zm-2.3893.34597c.2837-1.08729 1.2656-1.84597 2.3893-1.84597 1.5927 0 2.7689 1.48562 2.4034 3.03586l-1.0159 4.3089.8869.14174.0228.0034c.942.1413 1.6852.2528 2.2762.3812.6007.1305 1.1027.2898 1.5438.5541 1.2775.7653 2.2994 1.9882 2.2994 3.7985 0 1.087-.2625 1.871-.7774 3.4089-.0421.1259-.086.2568-.1315.3934-.0131.0392-.026.078-.0387.1162-.3539 1.0623-.5809 1.7438-.9606 2.2968-.6238.9084-1.544 1.5716-2.6031 1.8761-.6446.1854-1.3629.1853-2.4826.185-.0403 0-.0811 0-.1225 0h-1.0489c-.0534 0-.1061 0-.1582 0-1.4924.0005-2.4563.0007-3.3277-.3584-.156-.0643-.3084-.1369-.4566-.2174-.82829-.4498-1.43593-1.1981-2.37673-2.3567-.03281-.0404-.06602-.0813-.09967-.1227l-3.3585-4.1335c-.73863-.9091-.74356-2.2102-.01183-3.1248.87949-1.0994 2.49492-1.2512 3.56385-.335l1.22294 1.0483z" />
                      <path d="m8.70377 1.56279c-.01348.00107-.02696.00213-.04043.0032l-1.34747.10646c-.41292.03263-.72122.39382-.68859.80674.03262.41293.39381.72123.80674.6886l.66705-.0527-1.74263 1.74262c-.29289.2929-.29289.76777 0 1.06066.29289.2929.76777.2929 1.06066 0l1.74294-1.74293-.05275.66762c-.03263.41293.27567.77412.6886.80675.41291.03262.77411-.27568.80671-.6886l.1065-1.34747c.001-.01292.002-.02584.0031-.03876.0224-.28294.0448-.56615.0316-.79987-.0145-.25869-.0778-.60436-.3588-.88532-.281-.28106-.62677-.3442-.88542-.3587-.2331-.01306-.5156.00933-.79781.0317zm-3.06243 5.07289c-.29289-.2929-.76776-.29292-1.06066-.00003l-1.74272 1.74262.0527-.66713c.03263-.41292-.27567-.77411-.68859-.80674-.41293-.03262-.77412.27567-.80675.6886l-.10646 1.34747c-.0011.01393-.0022.02788-.00331.04182-.02233.28163-.04468.56362-.03173.79626.01439.25845.07729.60455.35858.88585.28119.2812.62723.3442.88574.3587.23327.0131.51595-.0093.79831-.0317.01339-.0011.02678-.0021.04016-.0032l1.34747-.1064c.41293-.0327.72122-.3939.6886-.8068-.03263-.41292-.39382-.72121-.80674-.68858l-.66755.05274 1.74292-1.74282c.2929-.29289.29291-.76776.00003-1.06066z" />
                    </g>
                  </svg>
                  <div className="mt-2 text-sm text-white">Pinch to zoom</div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="xl:h-[100vh] xl:w-[100vh]">
            {/* @ts-ignore */}
            <Magnifier
              src={imageList[currentIndex]}
              // @ts-ignore
              alt={`Product ${currentIndex + 1}`}
              zoomFactor={1.1}
              mgShape="square"
              className="cursor-pointer object-contain"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailImageCarousel;
